import { useState, useRef, useEffect } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { Logo } from './Logo';
import { useAuth } from '../lib/auth';

export function TopBar() {
  const { user, logout } = useAuth();
  const [open, setOpen] = useState(false);
  const menuRef = useRef<HTMLDivElement>(null);
  const loc = useLocation();

  useEffect(() => {
    function onClick(e: MouseEvent) {
      if (menuRef.current && !menuRef.current.contains(e.target as Node)) {
        setOpen(false);
      }
    }
    if (open) document.addEventListener('mousedown', onClick);
    return () => document.removeEventListener('mousedown', onClick);
  }, [open]);

  const navItem = (to: string, label: string) => (
    <Link
      to={to}
      style={{
        padding: '6px 10px',
        borderRadius: 6,
        color: loc.pathname === to ? 'var(--text)' : 'var(--text-dim)',
        background: loc.pathname === to ? 'var(--accent-soft)' : 'transparent',
        fontSize: 13,
        fontWeight: 500,
        transition: 'all 80ms ease',
      }}
    >
      {label}
    </Link>
  );

  return (
    <header
      className="rl-topbar"
      style={{
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'space-between',
        padding: '14px 24px',
        borderBottom: '1px solid var(--border)',
        background: 'var(--topbar-bg)',
        backdropFilter: 'blur(8px)',
        position: 'sticky',
        top: 0,
        zIndex: 10,
        gap: 16,
      }}
    >
      <div style={{ display: 'flex', alignItems: 'center', gap: 24, minWidth: 0 }}>
        <Logo size="sm" linkTo={user ? '/dashboard' : 'https://riffloop.app/'} />
        {user && (
          // Inline nav on wider screens; on phones it's hidden (rl-topnav) and
          // these links move into the avatar dropdown (rl-mobile-nav) so the
          // header can't overflow the viewport.
          <nav className="rl-topnav">
            {navItem('/dashboard', 'Home')}
            {navItem('/', 'Studio')}
            {navItem('/sections', 'Library')}
            {navItem('/pricing', 'Plans')}
            {navItem('/support', 'Support')}
          </nav>
        )}
      </div>

      <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
        <ThemeToggle />
        {user ? (
          <>
            {user.plan === 'free' && loc.pathname !== '/pricing' && (
              <Link
                to="/pricing"
                className="rl-btn-primary"
                style={{ textDecoration: 'none', padding: '7px 14px', fontSize: 13, whiteSpace: 'nowrap' }}
              >
                Upgrade
              </Link>
            )}
          <div style={{ position: 'relative' }} ref={menuRef}>
            <button
              onClick={() => setOpen(!open)}
              aria-label="User menu"
              style={{
                background: 'transparent',
                border: '1px solid var(--border-strong)',
                borderRadius: 999,
                padding: 2,
                cursor: 'pointer',
                display: 'flex',
                alignItems: 'center',
              }}
            >
              {user.picture_url ? (
                <img
                  src={user.picture_url}
                  alt={user.name}
                  style={{ width: 28, height: 28, borderRadius: '50%', display: 'block' }}
                />
              ) : (
                <div
                  style={{
                    width: 28,
                    height: 28,
                    borderRadius: '50%',
                    background: 'var(--accent-gradient)',
                    color: '#fff',
                    fontSize: 12,
                    fontWeight: 700,
                    display: 'grid',
                    placeItems: 'center',
                  }}
                >
                  {user.name?.[0]?.toUpperCase() ?? '?'}
                </div>
              )}
            </button>
            {open && (
              <div
                style={{
                  position: 'absolute',
                  top: 'calc(100% + 6px)',
                  right: 0,
                  width: 220,
                  background: 'var(--panel)',
                  border: '1px solid var(--border-strong)',
                  borderRadius: 'var(--radius)',
                  boxShadow: '0 8px 24px rgba(0,0,0,0.4)',
                  padding: 6,
                  zIndex: 20,
                }}
              >
                <div
                  style={{
                    padding: '8px 10px 10px',
                    borderBottom: '1px solid var(--border)',
                    marginBottom: 4,
                  }}
                >
                  <div style={{ fontSize: 13, fontWeight: 600 }}>{user.name}</div>
                  <div style={{ fontSize: 11, color: 'var(--text-dim)' }}>{user.email}</div>
                  <div
                    style={{
                      marginTop: 6,
                      display: 'inline-block',
                      padding: '2px 6px',
                      fontSize: 9,
                      fontWeight: 700,
                      letterSpacing: 0.5,
                      background: user.plan === 'pro' ? 'var(--accent-soft-2)' : 'var(--panel-2)',
                      color: user.plan === 'pro' ? 'var(--accent-purple)' : 'var(--text-dim)',
                      borderRadius: 4,
                      textTransform: 'uppercase',
                    }}
                  >
                    {user.plan}
                  </div>
                </div>
                {/* Primary nav — only rendered into the menu on phones, where the
                    inline TopBar nav is hidden. */}
                <div className="rl-mobile-nav">
                  <MenuLink to="/dashboard" onClick={() => setOpen(false)}>
                    Home
                  </MenuLink>
                  <MenuLink to="/" onClick={() => setOpen(false)}>
                    Studio
                  </MenuLink>
                  <MenuLink to="/sections" onClick={() => setOpen(false)}>
                    Library
                  </MenuLink>
                  <MenuLink to="/pricing" onClick={() => setOpen(false)}>
                    Plans
                  </MenuLink>
                  <div style={{ height: 1, background: 'var(--border)', margin: '4px 2px' }} />
                </div>
                <MenuLink to="/settings" onClick={() => setOpen(false)}>
                  Settings
                </MenuLink>
                <MenuLink to="/billing" onClick={() => setOpen(false)}>
                  Billing
                </MenuLink>
                <MenuLink to="/support" onClick={() => setOpen(false)}>
                  Support
                </MenuLink>
                <button
                  onClick={logout}
                  style={menuButtonStyle}
                  onMouseEnter={(e) => (e.currentTarget.style.background = 'var(--panel-2)')}
                  onMouseLeave={(e) => (e.currentTarget.style.background = 'transparent')}
                >
                  Sign out
                </button>
              </div>
            )}
          </div>
          </>
        ) : (
          // Logged out: /pricing is a logged-in-only upgrade page, so there are
          // no nav links — just a Sign in CTA. Hide it on /login itself, where
          // it's redundant (this IS the sign-in page); the theme toggle stays.
          loc.pathname !== '/login' && (
            <Link to="/login" className="rl-btn-primary" style={{ textDecoration: 'none' }}>
              Sign in
            </Link>
          )
        )}
      </div>
    </header>
  );
}

const menuButtonStyle: React.CSSProperties = {
  display: 'block',
  width: '100%',
  textAlign: 'left',
  padding: '8px 10px',
  background: 'transparent',
  border: 'none',
  color: 'var(--text)',
  fontSize: 13,
  borderRadius: 4,
  cursor: 'pointer',
};

function MenuLink({ to, children, onClick }: { to: string; children: React.ReactNode; onClick: () => void }) {
  return (
    <Link
      to={to}
      onClick={onClick}
      style={{ ...menuButtonStyle, textDecoration: 'none' }}
      onMouseEnter={(e) => (e.currentTarget.style.background = 'var(--panel-2)')}
      onMouseLeave={(e) => (e.currentTarget.style.background = 'transparent')}
    >
      {children}
    </Link>
  );
}

// Global light/dark switch. The actual theme is applied to <html data-theme>
// (set pre-paint by the inline script in index.html); this just flips it and
// persists the choice. Shown in the TopBar on every page, logged in or out.
function ThemeToggle() {
  const [theme, setTheme] = useState<'dark' | 'light'>(() =>
    typeof document !== 'undefined' && document.documentElement.getAttribute('data-theme') === 'light' ? 'light' : 'dark',
  );
  const toggle = () => {
    const next = theme === 'dark' ? 'light' : 'dark';
    setTheme(next);
    document.documentElement.setAttribute('data-theme', next);
    try { localStorage.setItem('rl-theme', next); } catch { /* ignore */ }
  };
  return (
    <button
      onClick={toggle}
      aria-label={theme === 'dark' ? 'Switch to light mode' : 'Switch to dark mode'}
      title={theme === 'dark' ? 'Switch to light mode' : 'Switch to dark mode'}
      style={{
        background: 'transparent',
        border: '1px solid var(--border-strong)',
        borderRadius: 999,
        width: 34,
        height: 34,
        display: 'grid',
        placeItems: 'center',
        cursor: 'pointer',
        color: 'var(--text-dim)',
        flexShrink: 0,
      }}
    >
      {theme === 'dark' ? (
        // Sun (click → go light)
        <svg width="17" height="17" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
          <circle cx="12" cy="12" r="4.5" />
          <path d="M12 2v2M12 20v2M4.9 4.9l1.4 1.4M17.7 17.7l1.4 1.4M2 12h2M20 12h2M4.9 19.1l1.4-1.4M17.7 6.3l1.4-1.4" />
        </svg>
      ) : (
        // Moon (click → go dark)
        <svg width="17" height="17" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
          <path d="M21 12.8A9 9 0 1 1 11.2 3a7 7 0 0 0 9.8 9.8Z" />
        </svg>
      )}
    </button>
  );
}
